import { gql as apolloGql, type TypedDocumentNode, useApolloClient, useMutation } from "@apollo/client";
import { decodeJwt } from "jose";
import Head from "next/head";
import { useRouter } from "next/router";
import { useCallback, useRef, useState } from "react";
import { Theme } from "tamagui";
import { Lock as LockIcon } from "@tamagui/lucide-icons";
import { analyticsService } from "@medbillai/app/lib/analytics";
import { sentryService } from "@medbillai/app/lib/sentry";
import { anonymousSessionStorage } from "@medbillai/app/lib/storage";
import { useDocumentUpload } from "@medbillai/app/provider/documents/UploadDocumentProvider";
import { createDocumentAnalysisEvent, createDocumentUploadEvent } from "@medbillai/app/screens/case/utils/createPreviewEvents";
import { getIssueQuery, getIssueQueryVariables } from "@medbillai/app/screens/case/utils/queries";
import { type GetIssueQuery, type OpenNewIssueFromPreviewInput, PreviewMessageType } from "@medbillai/graphql-types";
import { Body2, FullscreenSpinnerOverlay, Image, Title4, Title5, XStack, YStack } from "@medbillai/ui";
import { preAuthCaseUrl } from "@medbillai/utils/urls";
import type { NextPageWithLayout } from "../_app";
const ACCEPTED_MIME_TYPES = new Set(["application/pdf", "image/jpeg", "image/png", "image/heic"]);
const ACCEPTED_EXTENSIONS = new Set([".pdf", ".jpg", ".jpeg", ".png", ".heic"]);
type SignAnonymousSessionData = {
  signAnonymousSession: string;
};
type SignAnonymousSessionVars = {
  sessionId: string;
};
const signAnonymousSessionMutation: TypedDocumentNode<SignAnonymousSessionData, SignAnonymousSessionVars> = apolloGql`
  mutation SignAnonymousSessionForBillUpload($sessionId: String!) {
    signAnonymousSession(sessionId: $sessionId)
  }
`;
type OpenNewIssueForBillUploadData = {
  openNewIssueFromPreview?: {
    id: string;
  } | null;
};
type OpenNewIssueForBillUploadVars = {
  input: OpenNewIssueFromPreviewInput;
};
const openNewIssueFromPreviewForBillUploadMutation: TypedDocumentNode<OpenNewIssueForBillUploadData, OpenNewIssueForBillUploadVars> = apolloGql`
  mutation OpenNewIssueFromPreviewForBillUpload($input: OpenNewIssueFromPreviewInput!) {
    openNewIssueFromPreview(input: $input) {
      id
    }
  }
`;
const BillUploadPage: NextPageWithLayout = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const client = useApolloClient();
  const {
    upload
  } = useDocumentUpload();
  const [signAnonymousSession] = useMutation(signAnonymousSessionMutation);
  const [openNewIssueFromPreview] = useMutation(openNewIssueFromPreviewForBillUploadMutation);
  const postMessageToParent = useCallback((data: Record<string, unknown>) => {
    window.parent.postMessage({
      type: "granted-health:file-uploaded",
      ...data
    }, "*");
  }, []);
  const handleUpload = useCallback(async (file: File) => {
    setIsLoading(true);
    setError(null);
    try {
      const anonymousId = analyticsService.getAnonymousId() ?? crypto.randomUUID();
      const {
        data: sessionData
      } = await signAnonymousSession({
        variables: {
          sessionId: anonymousId
        }
      });
      if (!sessionData?.signAnonymousSession) {
        throw new Error("Failed to create anonymous session");
      }
      const jwt = sessionData.signAnonymousSession;
      await anonymousSessionStorage.set("token", jwt);
      let decodedUserId: string | null = null;
      try {
        const payload = decodeJwt(jwt);
        if (typeof payload.userId === "string") {
          decodedUserId = payload.userId;
          await anonymousSessionStorage.set("userId", payload.userId);
        }
      } catch (decodeErr) {
        sentryService.captureException(decodeErr);
      }
      let uploadedDocumentId: string | null = null;
      await upload({
        key: anonymousId,
        uploadFiles: [{
          name: file.name,
          sizeBytes: file.size,
          contentType: file.type || "application/octet-stream",
          blob: file
        }],
        onUploadStarted: () => {},
        onUploadComplete: (documentId: string) => {
          uploadedDocumentId = documentId;
          return Promise.resolve();
        },
        onUploadError: err => {
          throw err.error ?? new Error(err.message ?? "Upload failed");
        }
      });
      if (!uploadedDocumentId) {
        throw new Error("Upload did not complete successfully");
      }
      const {
        data: issueData
      } = await openNewIssueFromPreview({
        variables: {
          input: {
            caseType: "BILL_DISPUTE",
            title: file.name,
            isFromUpload: true,
            messages: [{
              type: PreviewMessageType.USER,
              userMessageInfo: {
                documentId: uploadedDocumentId
              }
            }]
          }
        }
      });
      const issueId = issueData?.openNewIssueFromPreview?.id;
      if (!issueId) {
        throw new Error("Failed to create case");
      }

      // Write a minimal issue entry to the Apollo cache so the case page
      // renders immediately from cache instead of showing a second spinner.
      if (decodedUserId) {
        const docUploadEvent = createDocumentUploadEvent();
        const docAnalysisEvent = createDocumentAnalysisEvent();
        client.writeQuery<GetIssueQuery>({
          query: getIssueQuery,
          variables: getIssueQueryVariables(issueId, decodedUserId),
          data: {
            __typename: "Query",
            issue__Issue: [{
              __typename: "issue__Issue_Type",
              id: issueId,
              title: file.name,
              openedAt: new Date().toISOString(),
              closedAt: null,
              isEscalated: false,
              caseType: "BILL_DISPUTE",
              benefitsQuestionType: null,
              billDisputeType: null,
              additionalDetails: null,
              benefitsInclude: null,
              benefitsPrice: null,
              benefitsCoverage: null,
              userVisibleEvents: [docUploadEvent, docAnalysisEvent],
              lastEvent: docAnalysisEvent,
              lastNonUserEvent: null,
              firstUnseenEvent: null,
              hasStaffParticipant: false
            }],
            me: {
              __typename: "User_Type",
              webAgentLinks: []
            }
          }
        });
      }
      postMessageToParent({
        documentId: uploadedDocumentId,
        issueId
      });
      await router.push(preAuthCaseUrl(issueId, decodedUserId ? {
        userId: decodedUserId
      } : undefined));
    } catch (err) {
      setIsLoading(false);
      setError("Something went wrong. Please try again.");
      sentryService.captureException(err);
    }
  }, [upload, signAnonymousSession, openNewIssueFromPreview, postMessageToParent, router, client]);
  const handleFileChange = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    if (files.length > 1) {
      setError("Please upload only one file");
      return;
    }
    const file = files[0];
    if (!file) {
      return;
    }
    const ext = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
    if (!ACCEPTED_MIME_TYPES.has(file.type) && !ACCEPTED_EXTENSIONS.has(ext)) {
      setError("Unsupported file type. Please upload a PDF, JPG, HEIC, or PNG");
      return;
    }
    setError(null);
    void handleUpload(file);
  }, [handleUpload]);
  const handleChooseFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files);
  }, [handleFileChange]);
  if (isLoading) {
    return <FullscreenSpinnerOverlay overlayColor="$background" />;
  }
  return <Theme name="v2" data-sentry-element="Theme" data-sentry-component="BillUploadPage" data-sentry-source-file="bill-upload.tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="bill-upload.tsx">
        <title>Upload your bill</title>
      </Head>

      <YStack flex={1} alignItems="center" justifyContent="center" style={{
      minHeight: "100vh"
    }} data-sentry-element="YStack" data-sentry-source-file="bill-upload.tsx">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop zone */}
        <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
          <YStack width={357} borderWidth="$0.5" borderStyle={isDragging ? "solid" : "dashed"} borderColor={isDragging ? "$textPrimary" : error ? "$textPrimary" : "$textSecondary"} borderRadius="$5" padding="$sp3" alignItems="center" justifyContent="center" style={{
          transition: "border-color 0.15s ease, background-color 0.15s ease",
          backgroundColor: isDragging ? "#F5F4F1" : "transparent",
          minHeight: 432
        }} data-sentry-element="YStack" data-sentry-source-file="bill-upload.tsx">
            {isDragging ? <span style={{
            background: "linear-gradient(to right, #e3fab4, #d8fb8e)",
            borderRadius: 999,
            padding: "6px 16px",
            display: "inline-block"
          }}>
                <Title5 color="$textPrimary">Drop your bill anywhere</Title5>
              </span> : <>
                <YStack flex={1} justifyContent="center" alignItems="center">
                  <Image source={{
                uri: error ? "/static/billUploadErrorIllustration.png" : "/static/billUploadIllustration.png"
              }} width={96} height={105} objectFit="contain" backgroundColor="transparent" alt="Bill upload illustration" />
                  <Title4 color="$textPrimary" textAlign="center" marginBottom="$sp1">
                    {error ? "Upload failed" : "Drop a medical bill to start"}
                  </Title4>
                  {error ? <Body2 color="$alertNegative" textAlign="center">
                      {error}
                    </Body2> : null}
                </YStack>

                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.heic,.png" style={{
              display: "none"
            }} onChange={e => {
              handleFileChange(e.target.files);
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }} />

                <YStack flex={1} flexGrow={1} justifyContent="space-between" alignItems="center">
                  <YStack alignItems="center">
                    <button type="button" onClick={handleChooseFile} style={{
                  position: "relative",
                  width: 163,
                  height: 70,
                  cursor: "pointer",
                  marginTop: 4,
                  background: "none",
                  border: "none",
                  padding: 0
                }}>
                      <div style={{
                    position: "absolute",
                    top: 6,
                    left: 0,
                    width: 163,
                    height: 57,
                    background: "linear-gradient(to right, #e3fab4, #d8fb8e)",
                    border: "0.5px solid #cef083",
                    borderRadius: 999,
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)"
                  }} />
                      <div style={{
                    position: "absolute",
                    top: 16,
                    left: 29,
                    width: 105,
                    height: 37,
                    background: "linear-gradient(to right, #e3fab4, #d8fb8e)",
                    borderRadius: 999
                  }} />
                      <Title5 color="$textPrimary" tag="span" style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    pointerEvents: "none",
                    whiteSpace: "nowrap"
                  }}>
                        Choose file
                      </Title5>
                    </button>
                    <Body2 mt="$sp1" color="$textSecondary" textAlign="center">
                      Supports PDF, JPG, HEIC, PNG
                    </Body2>
                  </YStack>
                  <XStack alignItems="center" gap="$sp1">
                    <LockIcon size="$iconMedium" color="$textSecondary" />
                    <Body2 color="$textSecondary">
                      HIPAA-aligned & encrypted for privacy
                    </Body2>
                  </XStack>
                </YStack>
              </>}
          </YStack>
        </div>
      </YStack>
    </Theme>;
};
export default BillUploadPage;